import type { Skill } from '@clockwise/clients/skills';
import { CheckRow, Combobox } from '@/components/custom/combobox';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';
import { skillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// Skill picker for inline add on the user detail page. `excludeIds` lets the
// caller suppress skills already assigned. See `useEntityAutocomplete` for
// `loadStrategy` semantics; defaults to 'lazy' (server-side `q` search) so a
// large skill catalog can't outrun the preload page cap (200) and silently
// hide skills. `excludeIds` is folded into the lazy page size by the hook.
interface Props {
  value: string | null;
  onChange: (skill: Skill | null) => void;
  disabled?: boolean;
  placeholder?: string;
  excludeIds?: readonly string[];
  className?: string;
  loadStrategy?: LoadStrategy;
}

const getSkillId = (s: Skill) => s.id;
const skillLabel = (s: Skill) => s.name;
const skillFilter = (s: Skill, q: string) => {
  const hay = `${s.name} ${s.description ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};
const fetchSkillList = ({ q, limit }: { q?: string; limit: number }) =>
  skillsApi.list({
    q,
    active: true,
    sortField: 'name',
    sortDirection: 'asc',
    limit,
    offset: 0,
  });
const fetchSkillOne = (id: string) => skillsApi.get(id);
const skillListKey = ({ q, limit }: { q?: string; limit: number }) =>
  queryKeys.skills.list({
    q,
    active: true,
    sortField: 'name',
    sortDirection: 'asc',
    limit,
    offset: 0,
  });
const skillDetailKey = (id: string) => queryKeys.skills.detail(id);

export function SkillAutocomplete({
  value,
  onChange,
  disabled,
  placeholder = 'Select skill…',
  excludeIds,
  className,
  loadStrategy = 'lazy',
}: Props) {
  const auto = useEntityAutocomplete<Skill>({
    value,
    strategy: loadStrategy,
    excludeCount: excludeIds?.length ?? 0,
    fetchList: fetchSkillList,
    fetchOne: fetchSkillOne,
    listKey: skillListKey,
    detailKey: skillDetailKey,
  });

  const exclude = new Set(excludeIds ?? []);
  const items = auto.items.filter((s) => !exclude.has(s.id));

  return (
    <Combobox<Skill>
      items={items}
      selectedItem={auto.selectedItem}
      value={value}
      onChange={onChange}
      getId={getSkillId}
      getLabel={skillLabel}
      filterFn={auto.isClient ? skillFilter : undefined}
      onQueryChange={auto.isClient ? undefined : auto.setQuery}
      onOpenChange={auto.setOpen}
      loading={auto.loading}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      renderItem={(s, { isSelected }) => (
        <CheckRow isSelected={isSelected} label={s.name} secondary={s.description} />
      )}
    />
  );
}
